import { getCachedAuthSession } from 'services/authSessionCache'
import ApiError from 'services/ApiError'
import { get, post, put, del } from 'aws-amplify/api'

import { Feature } from 'types/Feature'

type HttpMethod = typeof get | typeof post | typeof put | typeof del
interface AmplifyResponse {
  statusCode: number
  body: {
    json: () => Promise<unknown>
  }
}

interface FetchOptions {
  apiName?: string
  method: HttpMethod
  path: string
  payload?: Record<string, unknown>
  showBannerError?: boolean
}

let serverErrorFn: (errorMessage: string) => void = () => {}
export const setServerErrorFn = (fn: (errorMessage: string) => void) => {
  serverErrorFn = fn
}

const processResponse = async (response: AmplifyResponse, showBannerError: boolean) => {
  if (response.statusCode >= 500) {
    if (showBannerError) { serverErrorFn('SERVER ERROR') }
    throw new ApiError('Server Error', response as unknown as Response)
  }

  if (response.statusCode === 401) {
    throw new ApiError('Unauthorized', response as unknown as Response)
  }

  if (response.statusCode === 400) {
    throw new ApiError('Bad Request', response as unknown as Response)
  }

  if (response.statusCode > 401 && response.statusCode < 500) {
    throw new ApiError('Client Error', response as unknown as Response)
  }

  if (response.statusCode === 204 || response.statusCode === 304) {
    return null
  }

  try {
    const data = await response.body.json()

    return data
  } catch {
    return null
  }
}

const fetchFromApi = async ({ apiName = 'dwaApi', method, path, payload, showBannerError = true }: FetchOptions) => {
  try {
    const session = await getCachedAuthSession()
    const token = session?.tokens?.idToken?.toString() || ''

    const options: Record<string, unknown> = {
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    }

    if (payload) {
      options.body = payload
    }

    const result = method({
      apiName,
      path,
      options
    })
    const response = await result.response as unknown as AmplifyResponse
    return processResponse(response, showBannerError)
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }

    // Amplify rejects result.response for non-2xx responses, attaching the HTTP response to the error.
    const amplifyResponse = (error as { response?: AmplifyResponse })?.response
    if (amplifyResponse?.statusCode) {
      return processResponse(amplifyResponse, showBannerError)
    }
    
    throw new ApiError('Network Error', error as Response)
  }
}

const ServerlessApi = {
  Features: {
    createDealerBetaFeature: (dealerId: string, code: string) =>
      fetchFromApi({ method: post, path: `/features/${code}/dealers/${dealerId}` }),

    deleteDealerBetaFeature: (dealerId: string, code: string) =>
      fetchFromApi({ method: del, path: `/features/${code}/dealers/${dealerId}` }),

    getDealerBetaFeature: (dealerId: string) =>
      fetchFromApi({ method: get, path: `/features?dealerId=${dealerId}` }),

    create: (payload: Record<string, unknown>) =>
      fetchFromApi({ method: post, path: '/features', payload }),

    delete: (dealerId: string, code: string) =>
      fetchFromApi({ method: del, path: `/features/${encodeURIComponent(code)}/dealers/${dealerId}` }),

    list: () =>
      fetchFromApi({ method: get, path: '/features' }),

    listDealerBetaFeatures: (code: string, dealerId: string) =>
      fetchFromApi({ method: get, path: `/features/${encodeURIComponent(code)}/dealers/${encodeURIComponent(dealerId)}` }),

    update: (code: string, payload: Partial<Feature>) =>
      fetchFromApi({ method: put, path: `/features/${encodeURIComponent(code)}`, payload })
  },
  ActiveAlerts: {
    get: (deviceId: string) => fetchFromApi({ method: get, path: `/active-alarms/${deviceId}` })
  },
  AlertHistory: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string) =>
      fetchFromApi({ method: get, path: `/alert-history/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}` })
  },
  AwairRth: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string, awairDeviceId: string) =>
      fetchFromApi({ method: get, path: `/awair-rth/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}&awairDevice=${awairDeviceId}` })
  },
  ConsumerRth: {
    get: (deviceId: string, granularity: string) =>
      fetchFromApi({ method: get, path: `/consumer-rth/${deviceId}/${granularity}` })
  },
  DemandResponse: {
    get: (deviceId: string) => fetchFromApi({ method: get, path: `/demand-response?deviceId=${deviceId}`, showBannerError: false })
  },
  HomeownerNotifiedAlerts: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string) =>
      fetchFromApi({ method: get, path: `/home-owner-notified/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}` })
  },
  RuntimeHistory: {
    get: (deviceId: string, startDate: string, endDate: string, timezone: string) =>
      fetchFromApi({ method: get, path: `/rth/${deviceId}?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}` })
  },
  Warranty: {
    get: (serialNumber: string) => fetchFromApi({ apiName: 'warrantyApi', method: get, path: `/warranties?serialNumber=${encodeURIComponent(serialNumber)}` })
  }
}

export default ServerlessApi
